import { useState, forwardRef } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import styles from "./NumberInput.module.css";

const NumberInput = forwardRef(
  (
    {
      value = "",
      onChange,
      onFocus,
      onBlur,
      name,
      id,
      min,
      max,
      step = 1,
      placeholder = "0",
      unit,
      showSteppers = true,
      disabled = false,
      readOnly = false,
      error = false,
      className = "",
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const numValue = value !== "" && value !== null && !isNaN(Number(value)) ? Number(value) : "";

    const handleStep = (direction) => {
      if (disabled || readOnly) return;
      const current = numValue === "" ? (min !== undefined ? min : 0) : numValue;
      let next = direction === "up" ? current + step : current - step;

      if (min !== undefined && next < min) next = min;
      if (max !== undefined && next > max) next = max;

      if (onChange) {
        onChange({ target: { name, value: next } });
      }
    };

    const handleChange = (e) => {
      const val = e.target.value;
      if (val === "") {
        onChange({ target: { name, value: "" } });
        return;
      }
      const parsed = Number(val);
      if (!isNaN(parsed)) {
        onChange({ target: { name, value: parsed } });
      }
    };

    const isMinReached = min !== undefined && numValue !== "" && numValue <= min;
    const isMaxReached = max !== undefined && numValue !== "" && numValue >= max;

    const wrapperClass = [
      styles.wrapper,
      isFocused ? styles.focused : "",
      error ? styles.hasError : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className={wrapperClass}>
        <input
          ref={ref}
          id={id || name}
          name={name}
          type="number"
          value={numValue}
          onChange={handleChange}
          onFocus={(e) => {
            setIsFocused(true);
            if (onFocus) onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          className={styles.input}
          {...props}
        />

        {unit && <span className={styles.unitBadge}>{unit}</span>}

        {showSteppers && !readOnly && (
          <div className={styles.stepperCol}>
            <button
              type="button"
              className={styles.stepperBtn}
              onClick={() => handleStep("up")}
              disabled={disabled || isMaxReached}
              tabIndex={-1}
              aria-label="Increment"
            >
              <ChevronUp size={12} />
            </button>
            <button
              type="button"
              className={styles.stepperBtn}
              onClick={() => handleStep("down")}
              disabled={disabled || isMinReached}
              tabIndex={-1}
              aria-label="Decrement"
            >
              <ChevronDown size={12} />
            </button>
          </div>
        )}
      </div>
    );
  }
);

NumberInput.displayName = "NumberInput";

export default NumberInput;
